import { useState } from "react";
import { Menu, X } from "lucide-react";
import SiteReveal from "../shared/SiteReveal";
import { BRAND as BASE_BRAND, NAV, COURSES, CHEF, PRIVATE } from "./hanokdamData";
import { getDemoProfile } from "../shared/industryCatalog";
import "../shared/siteReveal.css";
import "./HanokdamSite.css";

export default function HanokdamSite({ embed = false, profileId = "hanokdam" }) {
  const profile = getDemoProfile("restaurant", profileId);
  const BRAND = profile?.brand ?? BASE_BRAND;
  const theme = profile?.theme ?? {};
  const [menuOpen, setMenuOpen] = useState(false);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", date: "", course: "", guests: "2", request: "" });

  const scroll = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const submit = (e) => {
    e.preventDefault();
    setModal(true);
    setForm({ name: "", phone: "", date: "", course: "", guests: "2", request: "" });
  };

  return (
    <div
      className={`hd-site${embed ? " hd-site--embed" : ""}`}
      style={{
        "--hd-burgundy": theme.burgundy ?? "#5c1a1a",
        "--hd-gold": theme.gold ?? "#c9a962",
        "--hd-dark": theme.dark ?? "#1a1210",
        "--hd-cream": theme.cream ?? "#f5f0e8",
      }}
    >
      <header className="hd-header">
        <div className="hd-header__inner">
          <a href="#hd-top" className="hd-brand" onClick={(e) => { e.preventDefault(); scroll("hd-top"); }}>
            <strong>{BRAND.name}</strong>
            <span>{BRAND.nameEn}</span>
          </a>
          <nav className="hd-nav">
            {NAV.map((n) => (
              <a key={n.id} href={`#${n.id}`} onClick={(e) => { e.preventDefault(); scroll(n.id); }}>{n.label}</a>
            ))}
          </nav>
          <button type="button" className="hd-btn hd-btn--gold" onClick={() => scroll("reserve")}>예약하기</button>
          <button type="button" className="hd-menu" onClick={() => setMenuOpen((v) => !v)}>{menuOpen ? <X size={20} /> : <Menu size={20} />}</button>
        </div>
        {menuOpen && <nav className="hd-mobile">{NAV.map((n) => <a key={n.id} href={`#${n.id}`} onClick={(e) => { e.preventDefault(); scroll(n.id); }}>{n.label}</a>)}</nav>}
      </header>

      <section id="hd-top" className="hd-hero">
        <div className="hd-hero__overlay" />
        <div className="hd-wrap hd-hero__content">
          <span className="hd-eyebrow">Korean Fine Dining</span>
          <h1>{BRAND.slogan}</h1>
          <p>{BRAND.intro}</p>
          <button type="button" className="hd-btn hd-btn--gold" onClick={() => scroll("menu")}>코스 메뉴 보기</button>
        </div>
      </section>

      <section id="philosophy" className="hd-section">
        <div className="hd-wrap hd-philosophy">
          <SiteReveal>
            <div className="hd-philosophy__text">
              <span className="hd-eyebrow">Philosophy</span>
              <h2>재료와 시간에 대한 존중</h2>
              <p>한옥담은 국내산 제철 식재료만을 사용합니다. 장을 담그고 숙성하는 시간을 거쳐, 접시 하나하나에 계절의 이야기를 담습니다.</p>
            </div>
          </SiteReveal>
          <SiteReveal><div className="hd-philosophy__img" /></SiteReveal>
        </div>
      </section>

      <section id="menu" className="hd-section hd-section--dark">
        <div className="hd-wrap">
          <SiteReveal><div className="hd-head"><h2>코스 메뉴</h2><p>런치·디너 동일 코스 운영</p></div></SiteReveal>
          <div className="hd-courses">
            {COURSES.map((c) => (
              <SiteReveal key={c.name}>
                <article className={`hd-course${c.featured ? " hd-course--featured" : ""}`}>
                  {c.featured && <span className="hd-course__badge">Signature</span>}
                  <h3>{c.name}</h3>
                  <div className="hd-course__price">{c.price}</div>
                  <p>{c.desc}</p>
                  <ul>{c.items.map((i) => <li key={i}>{i}</li>)}</ul>
                  <button type="button" className="hd-btn hd-btn--outline" onClick={() => scroll("reserve")}>예약</button>
                </article>
              </SiteReveal>
            ))}
          </div>
        </div>
      </section>

      <section id="chef" className="hd-section">
        <div className="hd-wrap hd-chef">
          <SiteReveal><div className="hd-chef__img" /></SiteReveal>
          <SiteReveal>
            <div>
              <span className="hd-eyebrow">Chef</span>
              <h2>{CHEF.name}</h2>
              <span className="hd-chef__title">{CHEF.title}</span>
              <p>{CHEF.bio}</p>
            </div>
          </SiteReveal>
        </div>
      </section>

      <section id="private" className="hd-section hd-section--dark">
        <div className="hd-wrap">
          <SiteReveal><div className="hd-head"><h2>프라이빗 다이닝</h2></div></SiteReveal>
          <div className="hd-private">
            {PRIVATE.map((r) => (
              <SiteReveal key={r.name}>
                <article><h4>{r.name}</h4><span>{r.capacity}</span><p>{r.desc}</p></article>
              </SiteReveal>
            ))}
          </div>
        </div>
      </section>

      <section id="reserve" className="hd-section">
        <div className="hd-wrap hd-reserve">
          <SiteReveal>
            <div>
              <h2>예약 안내</h2>
              <p>{BRAND.address}</p>
              <p>{BRAND.hours}</p>
              <p>{BRAND.phone}</p>
              <p className="hd-reserve__note">코스 메뉴는 전원 사전 예약제입니다. 알레르기 정보를 함께 남겨 주세요.</p>
            </div>
          </SiteReveal>
          <SiteReveal>
            <form className="hd-form" onSubmit={submit}>
              <div className="hd-form-row">
                <div><label>이름</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><label>연락처</label><input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
              <div className="hd-form-row">
                <div><label>날짜</label><input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div>
                  <label>코스</label>
                  <select required value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })}>
                    <option value="">선택</option>
                    {COURSES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div><label>인원</label><select value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })}><option>2</option><option>3</option><option>4</option><option>5+</option></select></div>
              <div><label>요청사항</label><textarea value={form.request} onChange={(e) => setForm({ ...form, request: e.target.value })} placeholder="알레르기, 좌석 요청 등" /></div>
              <button type="submit" className="hd-btn hd-btn--gold">예약 신청</button>
            </form>
          </SiteReveal>
        </div>
      </section>

      <footer className="hd-footer"><div className="hd-wrap"><strong>{BRAND.name}</strong><span>© 2026 {BRAND.nameEn} · Portfolio by DSiteSolution</span></div></footer>

      {modal && (
        <div className="hd-modal-overlay" onClick={() => setModal(false)}>
          <div className="hd-modal" onClick={(e) => e.stopPropagation()}>
            <h3>예약이 접수되었습니다</h3>
            <p>확인 후 연락드리겠습니다.</p>
            <button type="button" className="hd-btn hd-btn--gold" onClick={() => setModal(false)}>확인</button>
          </div>
        </div>
      )}
    </div>
  );
}
